import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import {
  ACTIVITY_ENTITY_TYPES,
  NO_OUTCOME_KEY,
  AGGREGATION_KEY_SEPARATOR,
  type ActivityEntityType,
} from "@salestrak/widgets";
import type { Omittable } from "@salestrak/ui";

import { useMemberEntityResolver } from "#app/lib/data";
import { resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import type { OutcomeTone } from "#app/lib/outcome-tone";
import { normalizeOutcomeTone } from "#app/lib/outcome-tone";
import {
  useWorkspaceData,
  type WorkspaceReason,
} from "#app/lib/data/workspace-data-provider";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import { useActivityGroupCounts } from "../shared/use-activity-group-counts";
import { comparisonBasis, previousWindow } from "../shared/comparison-window";

export type OutcomePerformanceSort = "updates" | "positive-rate" | "completion";

export type OutcomeToneCounts = Record<OutcomeTone | "missing", number>;

export type OutcomePerformanceUserRow = OutcomeToneCounts & {
  userId: string;
  /** The member chip. `user: null` while the directory batch for an id outside
   * the workspace bootstrap page is in flight. */
  entity: EntityData;
  /** The row's words — the aria label and the drilldown chip both read it, so
   * an id the directory cannot answer for names the role instead of asserting
   * a person. */
  label: string;
  total: number;
  completed: number;
  positiveRate: number;
  completionRate: number;
};

export type OutcomePerformanceData = OutcomeToneCounts & {
  total: number;
  completed: number;
  positiveRate: number;
  completionRate: number;
  outcomeIdsByTone: Record<OutcomeTone | "missing", string[]>;
  users: OutcomePerformanceUserRow[];
  /** The outcome words behind the tones, over every actor in scope — largest
   *  first, the missing bucket last. The wide card lists them beside the band. */
  outcomes: OutcomeBreakdownRow[];
};

/** One outcome inside a breakdown — the subtable's line, the wide card's row. */
export type OutcomeBreakdownRow = {
  /** A reason id, or `NO_OUTCOME_KEY` for the Updates posted without one. */
  outcomeId: string;
  label: string;
  tone: OutcomeTone | "missing";
  count: number;
  /** Of the breakdown's total. */
  share: number;
};

/** The words for an outcome id the current catalog cannot name. The Update
 *  happened, so its row stays; only the name is gone. */
const UNAVAILABLE_OUTCOME_LABEL = "Unavailable outcome";
const MISSING_OUTCOME_LABEL = "No outcome";

/**
 * Fold a per-outcome count bucket into breakdown rows: largest first, the
 * missing bucket last whatever its size — it is the exception, not a member
 * of the distribution. An outcome the catalog no longer names keeps its row
 * under a stand-in label and counts as neutral, so history never disappears.
 */
function foldOutcomes(
  bucket: ReadonlyMap<string, number>,
  reasonById: ReadonlyMap<string, Pick<WorkspaceReason, "reasonId" | "tone" | "name">>,
  total: number,
): OutcomeBreakdownRow[] {
  const outcomes: OutcomeBreakdownRow[] = [];
  for (const [outcomeId, count] of bucket) {
    if (outcomeId === NO_OUTCOME_KEY) {
      outcomes.push({
        outcomeId,
        label: MISSING_OUTCOME_LABEL,
        tone: "missing",
        count,
        share: rate(count, total),
      });
      continue;
    }
    const reason = reasonById.get(outcomeId);
    outcomes.push({
      outcomeId,
      label: reason?.name ?? UNAVAILABLE_OUTCOME_LABEL,
      tone: reason ? normalizeOutcomeTone(reason.tone) : "neutral",
      count,
      share: rate(count, total),
    });
  }
  outcomes.sort((a, b) => {
    if (a.tone === "missing") return 1;
    if (b.tone === "missing") return -1;
    return b.count - a.count || a.label.localeCompare(b.label);
  });
  return outcomes;
}

const EMPTY_TONES: OutcomeToneCounts = {
  positive: 0,
  neutral: 0,
  negative: 0,
  missing: 0,
};

/** What an actor id the directory cannot answer for is called. Such an id is a
 * person we cannot name — not a person named "Unknown" — and the counts beside
 * it are exact either way, so the row states the role. */
const PENDING_MEMBER_LABEL = "Member";

function rate(part: number, whole: number): number {
  return whole === 0 ? 0 : part / whole;
}

function finishRow(
  userId: string,
  member: EntityData | null,
  tones: OutcomeToneCounts,
): OutcomePerformanceUserRow {
  const completed = tones.positive + tones.neutral + tones.negative;
  const total = completed + tones.missing;
  return {
    userId,
    entity: member ?? { kind: "user", id: userId, user: null },
    label: member ? resolveEntityLabel(member) : PENDING_MEMBER_LABEL,
    ...tones,
    total,
    completed,
    positiveRate: rate(tones.positive, completed),
    completionRate: rate(completed, total),
  };
}

/** Convert the exact `actor.outcome` aggregation into summary and member rows. */
export function buildOutcomePerformance(
  counts: Record<string, number>,
  reasons: readonly Pick<WorkspaceReason, "reasonId" | "tone" | "name">[],
  resolveMember: (userId: string) => EntityData | null,
  sortBy: OutcomePerformanceSort,
  minUpdates: number,
  limit: number,
): OutcomePerformanceData {
  const reasonById = new Map(reasons.map((reason) => [reason.reasonId, reason]));
  const toneByReason = new Map(
    reasons.map((reason) => [reason.reasonId, normalizeOutcomeTone(reason.tone)]),
  );
  const byUser = new Map<string, OutcomeToneCounts>();
  const byOutcome = new Map<string, number>();
  const outcomeIdsByTone: Record<OutcomeTone | "missing", Set<string>> = {
    positive: new Set(),
    neutral: new Set(),
    negative: new Set(),
    missing: new Set([NO_OUTCOME_KEY]),
  };

  for (const [key, count] of Object.entries(counts)) {
    const separator = key.indexOf(AGGREGATION_KEY_SEPARATOR);
    if (separator < 1) continue;
    const userId = key.slice(0, separator);
    const outcomeId = key.slice(separator + AGGREGATION_KEY_SEPARATOR.length);
    const tone =
      outcomeId === NO_OUTCOME_KEY
        ? "missing"
        : (toneByReason.get(outcomeId) ?? "neutral");
    outcomeIdsByTone[tone].add(outcomeId);
    byOutcome.set(outcomeId, (byOutcome.get(outcomeId) ?? 0) + count);
    const current = byUser.get(userId) ?? { ...EMPTY_TONES };
    current[tone] += count;
    byUser.set(userId, current);
  }

  const allRows = [...byUser.entries()].map(([userId, tones]) =>
    finishRow(userId, resolveMember(userId), tones),
  );
  const value = (row: OutcomePerformanceUserRow): number => {
    switch (sortBy) {
      case "positive-rate":
        return row.positiveRate;
      case "completion":
        return row.completionRate;
      case "updates":
        return row.total;
    }
  };
  const ranked = allRows
    .filter((row) => row.total >= minUpdates)
    .sort((a, b) => value(b) - value(a) || b.total - a.total)
    .slice(0, limit);
  const tones = allRows.reduce<OutcomeToneCounts>(
    (sum, row) => ({
      positive: sum.positive + row.positive,
      neutral: sum.neutral + row.neutral,
      negative: sum.negative + row.negative,
      missing: sum.missing + row.missing,
    }),
    { ...EMPTY_TONES },
  );
  const completed = tones.positive + tones.neutral + tones.negative;
  const total = completed + tones.missing;

  return {
    ...tones,
    total,
    completed,
    positiveRate: rate(tones.positive, completed),
    completionRate: rate(completed, total),
    outcomeIdsByTone: {
      positive: [...outcomeIdsByTone.positive],
      neutral: [...outcomeIdsByTone.neutral],
      negative: [...outcomeIdsByTone.negative],
      missing: [...outcomeIdsByTone.missing],
    },
    users: ranked,
    outcomes: foldOutcomes(byOutcome, reasonById, total),
  };
}

/** What each entity row is called. Plural: the row sums every Update posted
 *  on entities of the kind. */
export const ENTITY_TYPE_LABELS: Record<ActivityEntityType, string> = {
  project: "Projects",
};

/** One entity kind's Updates — the per-entity view's row, with the outcomes
 *  behind it ready for the disclosed subtable. Every kind gets a row, so a kind
 *  with no Updates yet still stands on the card with a zero. */
export type OutcomeEntityRow = OutcomeToneCounts & {
  entityType: ActivityEntityType;
  label: string;
  total: number;
  completed: number;
  positiveRate: number;
  completionRate: number;
  outcomes: OutcomeBreakdownRow[];
};

/**
 * Convert the exact `entityType.outcome` aggregation into one row per entity
 * kind, each carrying its tone totals and its outcome breakdown
 * ({@link foldOutcomes}).
 */
export function buildEntityOutcomes(
  counts: Record<string, number>,
  reasons: readonly Pick<WorkspaceReason, "reasonId" | "tone" | "name">[],
): OutcomeEntityRow[] {
  const reasonById = new Map(reasons.map((reason) => [reason.reasonId, reason]));
  const byEntity = new Map<ActivityEntityType, Map<string, number>>(
    ACTIVITY_ENTITY_TYPES.map((type) => [type, new Map()]),
  );
  for (const [key, count] of Object.entries(counts)) {
    const separator = key.indexOf(AGGREGATION_KEY_SEPARATOR);
    if (separator < 1) continue;
    const entityType = key.slice(0, separator);
    const outcomeId = key.slice(separator + AGGREGATION_KEY_SEPARATOR.length);
    const bucket = byEntity.get(entityType as ActivityEntityType);
    // A kind this client does not know is a newer server's row; it has no
    // line on the card, so it is not counted into someone else's.
    if (!bucket) continue;
    bucket.set(outcomeId, (bucket.get(outcomeId) ?? 0) + count);
  }
  return ACTIVITY_ENTITY_TYPES.map((entityType) => {
    const bucket = byEntity.get(entityType) ?? new Map<string, number>();
    const tones: OutcomeToneCounts = { ...EMPTY_TONES };
    const total = [...bucket.values()].reduce((sum, count) => sum + count, 0);
    const outcomes = foldOutcomes(bucket, reasonById, total);
    for (const outcome of outcomes) tones[outcome.tone] += outcome.count;
    const completed = tones.positive + tones.neutral + tones.negative;
    return {
      entityType,
      label: ENTITY_TYPE_LABELS[entityType],
      ...tones,
      total,
      completed,
      positiveRate: rate(tones.positive, completed),
      completionRate: rate(completed, total),
      outcomes,
    };
  });
}

/**
 * The per-entity view's read: ONE `entityType.outcome` aggregation over the
 * same Update stream the other views fold, so the entity rows and the summary
 * band on one board always sum to the same figure.
 */
export function useEntityOutcomePerformance(): Omittable<{
  rows: OutcomeEntityRow[];
  total: number;
  loading: boolean;
  error?: ErrorLike;
}> {
  const { reasons } = useWorkspaceData();
  const { where } = useScopedActivityWhere({ subject: { eq: "age" } });
  const { counts, ready, error } = useActivityGroupCounts({
    groupBy: "entityType.outcome",
    where,
  });
  const rows = useMemo(() => buildEntityOutcomes(counts, reasons), [counts, reasons]);
  const total = useMemo(() => rows.reduce((sum, row) => sum + row.total, 0), [rows]);
  return { rows, total, loading: !ready, error };
}

export function useOutcomePerformance(
  sortBy: OutcomePerformanceSort,
  minUpdates: number,
  limit: number,
): Omittable<{
  data: OutcomePerformanceData;
  loading: boolean;
  error?: ErrorLike;
}> {
  const { reasons } = useWorkspaceData();
  // Through the DIRECTORY, not `userMap`: the aggregation keys carry whichever
  // members posted Updates, and the bootstrap page answers for 500 of them in
  // name order — so which actors could be named came down to the alphabet.
  const resolveMember = useMemberEntityResolver();
  const { where } = useScopedActivityWhere({ subject: { eq: "age" } });
  const { counts, ready, error } = useActivityGroupCounts({
    groupBy: "actor.outcome",
    where,
  });
  // One card reads this twice — the header's totals line and the body — and
  // both must show one answer. Apollo serves the second `useActivityGroupCounts`
  // from its cache; this cache serves the second build from the first, keyed on
  // the count set the query answered, so the two readers never compute apart.
  const data = useMemo(
    () => cachedBuild(counts, reasons, resolveMember, sortBy, minUpdates, limit),
    [counts, limit, minUpdates, reasons, resolveMember, sortBy],
  );

  return { data, loading: !ready, error };
}

const BUILD_CACHE = new WeakMap<Record<string, number>, Map<string, OutcomePerformanceData>>();

function cachedBuild(
  counts: Record<string, number>,
  reasons: Parameters<typeof buildOutcomePerformance>[1],
  resolveMember: Parameters<typeof buildOutcomePerformance>[2],
  sortBy: OutcomePerformanceSort,
  minUpdates: number,
  limit: number,
): OutcomePerformanceData {
  const byArgs = BUILD_CACHE.get(counts) ?? new Map<string, OutcomePerformanceData>();
  BUILD_CACHE.set(counts, byArgs);
  // `reasons` and `resolveMember` change identity only when the workspace data
  // does, and that also re-answers `counts`, so the count set keys them too.
  const key = `${sortBy}|${minUpdates}|${limit}`;
  const hit = byArgs.get(key);
  if (hit) return hit;
  const built = buildOutcomePerformance(counts, reasons, resolveMember, sortBy, minUpdates, limit);
  byArgs.set(key, built);
  return built;
}

/** The Updates an outcome can land on: posted Project Updates. */
const OUTCOME_UPDATES = { subject: { eq: "age" } };

/** The positive share over the previous equal-length window, and the words
 *  that name it ("last week"). */
export type OutcomeComparison = {
  positive: number;
  completed: number;
  basis: string;
};

/** Fold an `outcome` aggregation into the two figures a positive rate needs. */
export function comparisonCounts(
  counts: Record<string, number>,
  reasons: readonly Pick<WorkspaceReason, "reasonId" | "tone">[],
): Pick<OutcomeComparison, "positive" | "completed"> {
  const toneByReason = new Map(
    reasons.map((reason) => [reason.reasonId, normalizeOutcomeTone(reason.tone)]),
  );
  let positive = 0;
  let completed = 0;
  for (const [outcomeId, count] of Object.entries(counts)) {
    if (outcomeId === NO_OUTCOME_KEY) continue;
    completed += count;
    if (toneByReason.get(outcomeId) === "positive") positive += count;
  }
  return { positive, completed };
}

/**
 * The card's comparison line: the same positive rate over the previous
 * equal-length window, read the way the metric and stat strip read theirs —
 * the previous figure beside its window, never a delta. All time has no
 * previous window, so it asks nothing and answers null.
 */
export function useOutcomeComparison(): Omittable<{
  comparison: OutcomeComparison | null;
  loading: boolean;
  error?: ErrorLike;
}> {
  const { reasons } = useWorkspaceData();
  const { dateRange, scope } = useScopedActivityWhere(OUTCOME_UPDATES);
  const basis = comparisonBasis(scope.dateRange.key);
  const override = useMemo(
    () => ({ dateRange: { key: "custom" as const, custom: previousWindow(dateRange) } }),
    [dateRange],
  );
  const { where } = useScopedActivityWhere(OUTCOME_UPDATES, { override });
  const { counts, ready, error } = useActivityGroupCounts({
    groupBy: "outcome",
    where,
    skip: basis === null,
  });
  const comparison = useMemo(
    () => (basis === null ? null : { ...comparisonCounts(counts, reasons), basis }),
    [basis, counts, reasons],
  );
  return { comparison, loading: basis !== null && !ready, error };
}
